/**
 * Reactions — reaction history + logging.
 *
 * Lists past reactions (GET /api/reactions/history) and lets the user log a new
 * one via ReactionForm. Opened as an overlay from the Dashboard's Reaction
 * History card.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import ReactionForm, { SYMPTOM_OPTIONS } from './ReactionForm';
import useAPI from '../../hooks/useAPI';

const SYMPTOM_LABEL = Object.fromEntries(SYMPTOM_OPTIONS.map((s) => [s.id, s.label]));

const sevBadge = (n) =>
  n >= 8
    ? { variant: 'danger', label: 'Severe' }
    : n >= 5
      ? { variant: 'warning', label: 'Moderate' }
      : { variant: 'success', label: 'Mild' };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export default function Reactions({ onClose }) {
  const { get } = useAPI();
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    get('/api/reactions/history?limit=50')
      .then((data) => setReactions(data.reactions || []))
      .catch(() => setError('Could not load your reaction history.'))
      .finally(() => setLoading(false));
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page-container">
      <div className="profile-head">
        <button className="icon-btn" onClick={onClose} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title profile-title">Reaction History</h1>
      </div>

      {showForm ? (
        <Card>
          <ReactionForm
            onLogged={() => {
              setShowForm(false);
              load();
            }}
            onCancel={() => setShowForm(false)}
          />
        </Card>
      ) : (
        <button className="btn btn-primary btn-full" onClick={() => setShowForm(true)}>
          <Plus size={18} /> Log a reaction
        </button>
      )}

      {loading ? (
        <Card><p className="text-center">Loading…</p></Card>
      ) : error ? (
        <Card><p className="text-center">{error}</p></Card>
      ) : reactions.length === 0 ? (
        <Card className="empty-state">
          <p className="empty-text">
            No reactions logged yet. If you ever have a reaction, log it here to
            build a record you can share with your allergist.
          </p>
        </Card>
      ) : (
        <ul className="reaction-list">
          {reactions.map((r) => {
            const sev = sevBadge(r.severity_score);
            return (
              <li key={r.id}>
                <Card className="reaction-item">
                  <div className="reaction-item-head">
                    <span className="reaction-item-product">
                      {r.product_name || 'Unknown product'}
                    </span>
                    <Badge variant={sev.variant}>
                      {sev.label} · {r.severity_score}/10
                    </Badge>
                  </div>
                  <p className="reaction-item-date">{fmtDate(r.reaction_occurred_at)}</p>
                  <div className="reaction-item-symptoms">
                    {(r.symptoms || []).map((s) => (
                      <span key={s} className="reaction-chip">
                        {SYMPTOM_LABEL[s] || s}
                      </span>
                    ))}
                  </div>
                  {(r.epigen_administered || r.emergency_services_called) && (
                    <div className="reaction-item-flags">
                      {r.epigen_administered && <span className="reaction-flag">💉 EpiPen used</span>}
                      {r.emergency_services_called && <span className="reaction-flag">🚑 911 called</span>}
                    </div>
                  )}
                  {r.notes && <p className="reaction-item-notes">“{r.notes}”</p>}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
